import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminTodoDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const [todos, setTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');

  // Fetch To-Dos
  const fetchTodos = async () => {
    try {
      const todosSnapshot = await getDocs(collection(db, 'todos'));
      const todosList = todosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      todosList.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setTodos(todosList);
    } catch (error) {
      console.error('Error fetching todos:', error);
    }
  };

  // Add To-Do
  const handleAddTodo = async () => {
    if (!newTodoText.trim()) return;

    try {
      await addDoc(collection(db, 'todos'), {
        text: newTodoText,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      setNewTodoText('');
      fetchTodos();
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

  // Toggle To-Do completion
  const handleToggleTodo = async (todoId, completed) => {
    try {
      await updateDoc(doc(db, 'todos', todoId), {
        completed: !completed,
        updatedAt: new Date().toISOString()
      });
      fetchTodos();
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  // Delete To-Do
  const handleDeleteTodo = async (todoId) => {
    try {
      await deleteDoc(doc(db, 'todos', todoId));
      fetchTodos();
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  // Fetch todos when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchTodos();
    }
  }, [isAuthenticated]);

  const pendingTodos = todos.filter(t => !t.completed);
  const completedTodos = todos.filter(t => t.completed);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">To-Do List Dashboard</h1>
        <p className="text-gray-400">Manage your tasks and stay organized</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 8a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Pending Tasks</p>
              <p className="text-2xl font-bold text-orange-400">{pendingTodos.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Completed Tasks</p>
              <p className="text-2xl font-bold text-green-400">{completedTodos.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-600 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-400">Completion Rate</p>
              <p className="text-2xl font-bold text-blue-400">
                {todos.length > 0 ? Math.round((completedTodos.length / todos.length) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add To-Do Section */}
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Add New Task</h2>
        <div className="flex space-x-4">
          <input
            type="text"
            value={newTodoText}
            onChange={(e) => setNewTodoText(e.target.value)}
            placeholder="Enter your task..."
            className="flex-1 p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
          />
          <button
            onClick={handleAddTodo}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors"
          >
            Add Task
          </button>
        </div>
      </div>

      {/* Tasks Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pending Tasks */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Pending Tasks</h2>
            <span className="text-sm text-orange-400 bg-orange-600/20 px-2 py-1 rounded">
              {pendingTodos.length}
            </span>
          </div>

          {pendingTodos.length > 0 ? (
            <div className="space-y-3">
              {pendingTodos.map(todo => (
                <div key={todo.id} className="flex items-center space-x-3 p-3 bg-[#2a2a2a] rounded-lg">
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={() => handleToggleTodo(todo.id, todo.completed)}
                    className="w-5 h-5 text-blue-600 bg-[#212121] border-[#4d4d4d] rounded focus:ring-blue-500"
                  />
                  <span className="flex-1 text-white">{todo.text}</span>
                  <button
                    onClick={() => handleDeleteTodo(todo.id)}
                    className="text-red-500 hover:text-red-400 p-1"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#2a2a2a] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 8a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No Pending Tasks</h3>
              <p className="text-gray-400">Great job! All caught up.</p>
            </div>
          )}
        </div>

        {/* Completed Tasks */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Completed Tasks</h2>
            <span className="text-sm text-green-400 bg-green-600/20 px-2 py-1 rounded">
              {completedTodos.length}
            </span>
          </div>

          {completedTodos.length > 0 ? (
            <div className="space-y-3">
              {completedTodos.slice(0, 10).map(todo => (
                <div key={todo.id} className="flex items-center space-x-3 p-3 bg-[#1a1a1a] rounded-lg opacity-60">
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={() => handleToggleTodo(todo.id, todo.completed)}
                    className="w-5 h-5 text-blue-600 bg-[#212121] border-[#4d4d4d] rounded focus:ring-blue-500"
                  />
                  <span className="flex-1 text-gray-400 line-through">{todo.text}</span>
                  <button
                    onClick={() => handleDeleteTodo(todo.id)}
                    className="text-red-500 hover:text-red-400 p-1"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                  </button>
                </div>
              ))}
              {completedTodos.length > 10 && (
                <p className="text-sm text-gray-400 text-center pt-2">
                  And {completedTodos.length - 10} more completed tasks...
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#2a2a2a] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No Completed Tasks Yet</h3>
              <p className="text-gray-400">Complete some tasks to see them here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Productivity Tips */}
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Productivity Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-[#2a2a2a] rounded-lg">
            <h3 className="font-medium text-blue-400 mb-2">Stay Focused</h3>
            <p className="text-sm text-gray-400">Break large tasks into smaller, manageable steps to maintain momentum.</p>
          </div>
          <div className="p-4 bg-[#2a2a2a] rounded-lg">
            <h3 className="font-medium text-green-400 mb-2">Review Regularly</h3>
            <p className="text-sm text-gray-400">Set aside time each week to review completed tasks and plan ahead.</p>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTodoDashboard;
